import createReducer from '../../../../../utils/createReducer';
import { ACTION_PREFIX } from '../../sportsbook-constants';
import selectSportsbookState from '../../sportsbook-state-selector';
import { RootState, ThunkAction } from '../../../../../store/types';
import {
	CalculateCashoutResponse,
	SubmitCashoutResponse,
	SubmitCashoutStatus
} from '../../api/betting-service/betting-service-types';
import {
	createSubscriptionToCashoutInformation,
	createSubscriptionToCashoutSubmits,
	disposeSubscriptionToCashoutInformation,
	disposeSubscriptionToCashoutSubmits,
	submitCashout as apiSubmitCashout
} from '../../api/betting-service-api';
import { CashoutInformation, CashoutSubmitStatus } from './cashout-types';
import { uniqueId } from '@comeon/utility';

type StateCashoutInformation = {
	betId: number;
	submitStatus: CashoutSubmitStatus;
	submitRef?: string;
	submittedAmount?: number;
	calculation: CalculateCashoutResponse;
	submit?: SubmitCashoutResponse;
};

type State = {
	informations: StateCashoutInformation[];
};

// Constants
const CASHOUT_CALCULATIONS_SUBSCRIPTION_REF = 'cashoutCalculation';
const CASHOUT_SUBMITS_SUBSCRIPTION_REF = 'cashoutSubmit';

// Actions
const DISPOSED = `${ACTION_PREFIX}CASHOUT/DISPOSED` as const;
type DisposedAction = {
	type: typeof DISPOSED;
};

const CALCULATIONS_RECEIVED = `${ACTION_PREFIX}CASHOUT/CALCULATIONS_RECEIVED` as const;
type CalculationsReceivedAction = {
	type: typeof CALCULATIONS_RECEIVED;
	payload: { calculations: Array<CalculateCashoutResponse> };
};

const SUBMIT_RECEIVED = `${ACTION_PREFIX}CASHOUT/SUBMIT_RECEIVED` as const;
type SubmitReceivedAction = {
	type: typeof SUBMIT_RECEIVED;
	payload: { submit: SubmitCashoutResponse };
};

const CASHOUT_SUBMIT_STARTED = `${ACTION_PREFIX}CASHOUT/CASHOUT_SUBMIT_STARTED` as const;
type CashoutSubmitStartedAction = {
	type: typeof CASHOUT_SUBMIT_STARTED;
	payload: { betId: number };
};

const CASHOUT_SUBMIT_REQUEST_STARTED = `${ACTION_PREFIX}CASHOUT/CASHOUT_SUBMIT_REQUEST_STARTED` as const;
type CashoutSubmitRequestStartedAction = {
	type: typeof CASHOUT_SUBMIT_REQUEST_STARTED;
	payload: { ref: string; betId: number; amount: number };
};

export const CASHOUT_NEW_OFFER_REJECTED = `${ACTION_PREFIX}CASHOUT/CASHOUT_NEW_OFFER_REJECTED` as const;
export type CashoutNewOfferRejectedAction = {
	type: typeof CASHOUT_NEW_OFFER_REJECTED;
	payload: { betId: number };
};

// Selectors
const selectState = (state: RootState) => selectSportsbookState(state).cashout;
export const selectAllCashoutInformations = (state: RootState): Array<CashoutInformation> =>
	selectState(state).informations.map(information => ({
		betId: information.betId,
		amount: [CashoutSubmitStatus.SUBMITTING, CashoutSubmitStatus.SUCCESS].includes(information.submitStatus)
			? information.submit?.amount || information.submittedAmount || 0
			: information.calculation.amount,
		submitStatus: information.submitStatus,
		newOfferAmount: information.submit?.newOfferAmount,
		errorDescription: information.submit?.errorDescription
	}));

// Action creators
export const initialize = (): ThunkAction<CalculationsReceivedAction> => dispatch => {
	createSubscriptionToCashoutInformation(
		CASHOUT_CALCULATIONS_SUBSCRIPTION_REF,
		data => dispatch({ type: CALCULATIONS_RECEIVED, payload: { calculations: data } }),
		// eslint-disable-next-line no-console
		console.error
	);
};

export const dispose = (): ThunkAction<DisposedAction> => dispatch => {
	disposeSubscriptionToCashoutInformation(CASHOUT_CALCULATIONS_SUBSCRIPTION_REF);
	disposeSubscriptionToCashoutSubmits(CASHOUT_SUBMITS_SUBSCRIPTION_REF);
	dispatch({ type: DISPOSED });
};

export const startSubmit = (betId: number): CashoutSubmitStartedAction => ({
	type: CASHOUT_SUBMIT_STARTED,
	payload: { betId }
});

export const confirmSubmit =
	(betId: number, amount: number): ThunkAction<CashoutSubmitRequestStartedAction | SubmitReceivedAction> =>
	async dispatch => {
		const ref = uniqueId(Date.now().toString());
		dispatch({ type: CASHOUT_SUBMIT_REQUEST_STARTED, payload: { ref, betId, amount } });
		const submit = await apiSubmitCashout({ ref, betId, amount });
		dispatch({ type: SUBMIT_RECEIVED, payload: { submit } });

		if (submit.status === SubmitCashoutStatus.DELAYED) {
			createSubscriptionToCashoutSubmits(CASHOUT_SUBMITS_SUBSCRIPTION_REF, data => {
				dispatch({ type: SUBMIT_RECEIVED, payload: { submit: data } });
			});
		}
	};

// Reducer
const initialState: State = {
	informations: []
};

const handleDisposed = (): State => initialState;

const handleCashoutCalculationsReceived = (
	state: State,
	{ payload: { calculations } }: CalculationsReceivedAction
): State => {
	const nextInformations = [...state.informations];

	calculations.forEach(calculation => {
		const informationIndex = nextInformations.findIndex(information => information.betId === calculation.betId);
		if (informationIndex !== -1) {
			const oldInformation = nextInformations[informationIndex];

			// Abort confirmation if calculation amount has changed
			const shouldAbortConfirmation =
				oldInformation.submitStatus === CashoutSubmitStatus.CONFIRMATION &&
				oldInformation.calculation.amount !== calculation.amount;

			// TODO Try to rely on CashoutSubmitStatus directly on the information instead.
			const isNewOffer = oldInformation.submit?.status === SubmitCashoutStatus.NEW_OFFER;

			const newSubmitStatus = shouldAbortConfirmation
				? CashoutSubmitStatus.NONE
				: isNewOffer
					? CashoutSubmitStatus.NEW_OFFER
					: oldInformation.submitStatus;

			nextInformations[informationIndex] = {
				...oldInformation,
				submitStatus: newSubmitStatus,
				calculation
			};

			return;
		}

		nextInformations.push({
			betId: calculation.betId,
			calculation,
			submitStatus: CashoutSubmitStatus.NONE
		});
	});

	return { ...state, informations: nextInformations };
};

const submitCashoutStatusToCashoutStatus = (cashoutStatus: SubmitCashoutStatus): CashoutSubmitStatus => {
	switch (cashoutStatus) {
		case SubmitCashoutStatus.DELAYED:
			return CashoutSubmitStatus.SUBMITTING;
		case SubmitCashoutStatus.SUCCESS:
			return CashoutSubmitStatus.SUCCESS;
		case SubmitCashoutStatus.NEW_OFFER:
			return CashoutSubmitStatus.NEW_OFFER;
		case SubmitCashoutStatus.ERROR:
		default:
			return CashoutSubmitStatus.ERROR;
	}
};

export const acceptNewOffer =
	(betId: number, newOfferAmount: number): ThunkAction<CashoutSubmitRequestStartedAction | SubmitReceivedAction> =>
	dispatch => {
		dispatch(confirmSubmit(betId, newOfferAmount));
	};

export const rejectNewOffer =
	(betId: number): ThunkAction<CashoutNewOfferRejectedAction> =>
	dispatch => {
		dispatch({
			type: CASHOUT_NEW_OFFER_REJECTED,
			payload: { betId }
		});
	};

const handleCashoutSubmitReceived = (state: State, { payload: { submit } }: SubmitReceivedAction): State => {
	const informationIndex = state.informations.findIndex(
		information =>
			information.betId === submit.betId && // Will always be true for the same betId
			information.submitRef === submit.ref // Only update the submit if it is the last known ref in the bet
	);

	if (informationIndex !== -1) {
		const newInformations = [...state.informations];
		newInformations[informationIndex] = {
			...newInformations[informationIndex],
			submit,
			submitStatus: submitCashoutStatusToCashoutStatus(submit.status)
		};

		return { ...state, informations: newInformations };
	}

	return state;
};

const handleCashoutSubmitStarted = (
	state: State,
	{ payload: { betId } }: CashoutSubmitRequestStartedAction
): State => ({
	...state,
	informations: state.informations.map(information =>
		information.betId === betId
			? { ...information, submitStatus: CashoutSubmitStatus.CONFIRMATION, submit: undefined }
			: information
	)
});

const handleCashoutSubmitRequestStarted = (
	state: State,
	{ payload: { ref, betId, amount } }: CashoutSubmitRequestStartedAction
): State => ({
	...state,
	informations: state.informations.map(information =>
		information.betId === betId
			? {
					...information,
					submitStatus: CashoutSubmitStatus.SUBMITTING,
					submitRef: ref,
					submittedAmount: amount
				}
			: information
	)
});

const handleCashoutNewOfferRejected = (state: State, { payload: { betId } }: CashoutNewOfferRejectedAction): State => ({
	...state,
	informations: state.informations.map(information =>
		information.betId === betId
			? {
					...information,
					submitStatus: CashoutSubmitStatus.NONE,
					submitRef: undefined,
					submittedAmount: undefined,
					submit: undefined
				}
			: information
	)
});

const reducer = createReducer(initialState, {
	[DISPOSED]: handleDisposed,
	[CALCULATIONS_RECEIVED]: handleCashoutCalculationsReceived,
	[SUBMIT_RECEIVED]: handleCashoutSubmitReceived,
	[CASHOUT_SUBMIT_STARTED]: handleCashoutSubmitStarted,
	[CASHOUT_SUBMIT_REQUEST_STARTED]: handleCashoutSubmitRequestStarted,
	[CASHOUT_NEW_OFFER_REJECTED]: handleCashoutNewOfferRejected
});

export default reducer;
